import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isEqual, isNil } from 'lodash'
import { IconLanguage, IconMinus, IconTrash } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { TextArea } from '@/components/forms/TextArea'
import Select from '@/components/forms/Select'
import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import { Loader } from '@/components/loaders/Loader'
import { Pill } from '@/components/pills/Pill'
import {
  fetchLanguageCatalog,
  fetchLanguageSettings,
  fetchLanguageVersions,
  previewLanguageInstructions,
  resetLanguageSettings,
  revertLanguageSettings,
  saveLanguageSettings,
} from '@/reducers/voiceAgents/voiceAgents.actions'
import { setError } from '@/reducers/voiceAgents/voiceAgents.redux'

import { formatVersionDate } from '../drawerHelpers'
import {
  formatLanguageOverride,
  isMultilingual,
  languageLabelFromCatalog,
  languagePreviewKey,
  parseLanguageVersionContent,
} from '../languageHelpers'
import { OverrideHistoryList } from './OverrideEditor'
import { SettingsField } from './SettingsField'
import VersionViewingStrip from './VersionViewingStrip'

// =============================================================================
// LanguageSettingsPanel — which languages the agent speaks and which it starts
// in (agent level only). Shares the override-editor card chrome and the
// history / peek / revert / reset UX with LlmSettingsPanel; the body is an
// ordered catalog multi-select plus a start-language select constrained to
// the chosen set, plus the LANGUAGE wording, which is generated from that set
// unless the agent owns its own. The BE derives the rest — the per-call
// language-detection config and the speech-recognition model, both sent at
// call time rather than written onto the provider agent. Nothing here reports
// or repairs provider state: since 2026-09-18 a save touches only the
// database, so there is none to drift.
//
// Order matters: `languages` is the operator's order and the first entry is
// the default start language. Never sort it.
// =============================================================================

const loadTarget = (dispatch, agentId) => {
  dispatch(fetchLanguageSettings(agentId))
  dispatch(fetchLanguageVersions(agentId))
}

const TEST_ID = 'languages'
// Long enough that picking three languages in a row is one request, short
// enough that the rules appear while the operator is still looking at them.
const PREVIEW_DEBOUNCE_MS = 300

// There is no SyncStatus component any more (2026-09-18). It rendered `drift`
// from the language payload and offered Re-apply, both of which existed because
// a save PATCHed the provider agent and could leave it out of step. That write
// is gone — settings reach Cartesia per call — so `drift`, `cartesia[]` and the
// sync route went with it. Nothing replaces the row: there is no provider state
// left to report.

const LanguageSettingsPanel = () => {
  const dispatch = useDispatch()
  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const catalog = useSelector((state) => state.voiceAgents.languageCatalog)
  const settings = useSelector((state) => state.voiceAgents.languageSettings)
  const versions = useSelector((state) => state.voiceAgents.languageVersions)
  const catalogLoading = useSelector((state) => Boolean(state.voiceAgents.loading.languageCatalog))
  const catalogError = useSelector((state) => state.voiceAgents.error.languageCatalog)
  const settingsError = useSelector((state) => state.voiceAgents.error.languageSettings)
  const isSaving = useSelector((state) => Boolean(state.voiceAgents.loading.saveLanguages))
  const isReverting = useSelector((state) => Boolean(state.voiceAgents.loading.revertLanguages))
  const isResetting = useSelector((state) => Boolean(state.voiceAgents.loading.resetLanguages))

  // The generated LANGUAGE block, off the agent config the page already loaded:
  // { enabled, languages, startLanguage, text, nodeIds }. The resolver injects
  // `text` into every node in `nodeIds` at call time — Studio shows it, and
  // there is nothing to edit.
  // Selected as the whole detail, not just the block: an absent detail is the
  // page's fetch still being in flight, and the card must not decide whether to
  // show itself on a request that hasn't answered.
  const agentDetail = useSelector(
    (state) => state.voiceAgents.detailById[state.voiceAgents.selectedAgentId]
  )
  const languageBlock = agentDetail?.languageBlock
  const preview = useSelector((state) => state.voiceAgents.languagePreview)
  const saveError = useSelector((state) => state.voiceAgents.error.saveLanguages)
  const revertError = useSelector((state) => state.voiceAgents.error.revertLanguages)
  const resetError = useSelector((state) => state.voiceAgents.error.resetLanguages)

  // Draft while editing; null = mirror the stored set.
  const [draft, setDraft] = useState(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [peekVersion, setPeekVersion] = useState(null)
  const [revertTarget, setRevertTarget] = useState(null)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)
  // Collapsed until asked for: most visits to this card are to check or change
  // the languages, and the rules are a paragraph. The state is still legible
  // while closed — the Generated / Custom pill sits in the label row — and the
  // effect below opens it when a language change rewrites the text.
  const [rulesOpen, setRulesOpen] = useState(false)
  // The generated wording, revealed on demand — only ever offered when it
  // differs from wording the agent owns.
  const [compareOpen, setCompareOpen] = useState(false)

  useEffect(() => {
    if (!catalog.loaded && !catalogLoading && !catalogError) {
      dispatch(fetchLanguageCatalog())
    }
  }, [dispatch, catalog.loaded, catalogLoading, catalogError])

  const draftLanguages = draft?.languages
  const draftStartLanguage = draft?.startLanguage

  useEffect(() => {
    if (isMultilingual(draftLanguages)) {
      setRulesOpen(true)
    }
  }, [draftLanguages])

  // The generated text for the pending set comes from the BE, so it is fetched
  // as the selection settles rather than on every keystroke of the controls.
  // Keyed on the two fields the preview is rendered from, not on `draft`: the
  // ASR select writes a new draft object while reusing the same `languages`
  // array, so depending on the object would re-request identical text.
  // Also fetched with no pending edit when the agent owns its wording: the
  // comparison needs the generated text for the *stored* set too. Skipped when
  // nothing is custom and nothing is drafted, where the field already holds the
  // generated text and a request would tell us what we have.
  const storedIsCustom = settings?.languageInstructionsCustom === true
  const previewLanguages = draftLanguages ?? (storedIsCustom ? settings?.languages : undefined)
  const previewStart = draftLanguages ? draftStartLanguage : settings?.startLanguage
  useEffect(() => {
    if (isEmpty(previewLanguages)) {
      return undefined
    }
    const timer = setTimeout(() => {
      dispatch(
        previewLanguageInstructions(agentId, {
          languages: previewLanguages,
          startLanguage: previewStart,
        })
      )
    }, PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [dispatch, agentId, previewLanguages, previewStart])

  // OverviewTab keys this panel by agent id, so switching agents remounts it
  // with fresh state — the mount fetch is all this has to do. (LlmSettingsPanel
  // clears its own state here because it also re-targets on `nodeId` without
  // remounting; this panel has no such case.)
  useEffect(() => {
    loadTarget(dispatch, agentId)
  }, [dispatch, agentId])

  // Expected-empty: the BE doesn't serve language settings for this agent (or
  // hasn't deployed the routes at all, which 404s the catalog too). Not an
  // error, so no card at all. Only hide while nothing is loaded — a late 404
  // for a previous agent must not blank an already-rendered panel.
  if (
    !settings &&
    (settingsError?.kind === 'unavailable' || catalogError?.kind === 'unavailable')
  ) {
    return null
  }

  const loadError = catalogError || (!settings && settingsError)
  if (loadError) {
    return (
      <div className="voice-agents__side-card" data-testid="language-panel-error">
        <ErrorMessage content="Couldn't load language settings." />
        <Button
          small
          onClick={() => {
            dispatch(fetchLanguageCatalog())
            loadTarget(dispatch, agentId)
          }}
        >
          Try again
        </Button>
      </div>
    )
  }
  if (!settings || !catalog.loaded || !agentDetail) {
    return <Loader centered label="Loading language settings…" />
  }

  // An agent without `language_block: true` never receives the wording, and
  // whether its language set reaches speech recognition depends on its own
  // pre_call_handler — which this payload does not report. So the card could
  // only ever have offered a setting with no effect it could name. Hidden
  // rather than annotated.
  //
  // Checked after the loading gate above, and as `=== false` rather than
  // truthily, because absent is not an opt-out: it means a config that predates
  // nothing — the BE builds this key on every response — so a missing field is
  // a shape we cannot explain and must not hide a working card over.
  //
  // Known consequence: an agent that wires the per-call handler without opting
  // into the block would lose its only control. No such agent exists today —
  // the one that wires it also sets `language_block: true`.
  if (languageBlock?.enabled === false) {
    return null
  }

  const { languages: catalogLanguages } = catalog
  const stored = {
    languages: settings.languages ?? [],
    startLanguage: settings.startLanguage ?? settings.languages?.[0] ?? null,
  }
  // The effective wording and whether the agent owns it. When it does not, this
  // text *is* the generated text, which is why editing it is how you take it over.
  const storedWording = settings.languageInstructions ?? ''
  const storedCustom = settings.languageInstructionsCustom === true
  // What the field shows before anyone touches it. `languageInstructions` is
  // null until an agent owns its wording — the generated text arrives on the
  // config block instead — so the settings field is the wrong thing to compare
  // an edit against: it would read every keystroke as taking ownership, and
  // typing then undoing would save a "custom" copy of the generated text.
  const untouchedWording = storedCustom ? storedWording : languageBlock?.text ?? ''
  // undefined = untouched, a string = edited, null = "go back to generated".
  const draftWording = draft?.languageInstructions
  const wordingTouched = draftWording !== undefined
  const restoringGenerated = draftWording === null
  const wordingDirty = restoringGenerated
    ? storedCustom
    : wordingTouched && draftWording !== untouchedWording
  const dirty =
    Boolean(draft) &&
    (wordingDirty ||
      !isEqual(
        {
          languages: draft.languages,
          startLanguage: draft.startLanguage,
        },
        stored
      ))

  const ordered = [...versions].sort((a, b) => (b.version || 0) - (a.version || 0))
  const latestVersionNumber = ordered[0]?.version
  const peekEntry = !isNil(peekVersion)
    ? ordered.find((entry) => entry.version === peekVersion)
    : null
  const isViewing = Boolean(peekEntry) && peekEntry.version !== latestVersionNumber
  const versionCount = ordered.length

  // What the controls show: a peeked version (read-only; a reset row shows the
  // baseline), otherwise the in-progress draft or the stored set.
  const shown = isViewing
    ? parseLanguageVersionContent(peekEntry.content) ?? settings.baseline ?? stored
    : draft ?? stored

  const historyEntries = ordered.map((entry) => {
    const formatted = formatLanguageOverride(
      parseLanguageVersionContent(entry.content),
      catalogLanguages
    )
    // The BE stamps reset rows with a summary equal to the formatted text, so
    // only append a summary that adds something.
    const summary = entry.summary && entry.summary !== formatted ? entry.summary : null
    return { ...entry, content: summary ? `${formatted} · ${summary}` : formatted }
  })
  const showResetRow = Boolean(settings.hasOverride) && !isViewing && historyOpen
  const controlsDisabled = isSaving || isViewing

  const languageOptions = catalogLanguages.map((entry) => ({
    value: entry.code,
    label: entry.label,
  }))
  const optionFor = (code) => ({
    value: code,
    label: languageLabelFromCatalog(catalogLanguages, code),
  })
  const startOptions = shown.languages.map(optionFor)
  const rulesText = languageBlock?.text ?? ''
  // Offered on any opted-in agent, single-language included: the block is
  // shorter there, but it is still the text the agent follows.
  const rulesAvailable = languageBlock?.enabled === true
  // The previewed text is only the pending set's text while it was rendered for
  // exactly that set \u2014 several previews can be in flight as the operator edits.
  // The block on the config describes the *saved* set, so a pending change is
  // answered by the preview route instead.
  const draftKey = languagePreviewKey(shown.languages, shown.startLanguage)
  // The preview route always renders the *generated* wording for a set, even
  // when the agent owns its own. That is deliberate on the BE: it is the only
  // thing owned wording can be compared against once it stops tracking the set.
  const previewedGenerated =
    preview?.key === draftKey && !isNil(preview.languageInstructions) && !isViewing
      ? preview.languageInstructions
      : null
  // Generated text for the set on screen. When nothing is custom the stored
  // text is itself generated, so it stands in until a preview lands.
  const generatedText = previewedGenerated ?? (storedCustom ? null : storedWording)
  const usePreview = dirty && !storedCustom && !isNil(previewedGenerated)
  // Owned wording must survive a language change — only generated text follows
  // the set. An explicit restore shows the generated text straight away.
  const rulesValue = (() => {
    if (wordingTouched && !restoringGenerated) {
      return draftWording
    }
    if (restoringGenerated) {
      return generatedText ?? storedWording
    }
    if (storedCustom) {
      return storedWording
    }
    return usePreview ? previewedGenerated : rulesText || storedWording
  })()
  // Custom once saved, or custom the moment this draft is saved.
  const willBeCustom = restoringGenerated ? false : storedCustom || wordingDirty
  // The whole safeguard: owned wording stops tracking the settings, so the only
  // way to notice is seeing what the settings would have said instead.
  const comparisonAvailable = willBeCustom && !isNil(generatedText) && generatedText !== rulesValue
  // The block is spliced in before variable substitution runs, so a `$` would
  // interpolate live call data into text we promise to use verbatim. The BE
  // rejects it; saying so here saves a round trip.
  const wordingHasDollar = willBeCustom && rulesValue.includes('$')
  // The wording a peeked version ran on. Absent means it had none of its own,
  // which is a different statement from "its wording was empty".
  const peekedWording = isViewing ? shown.instructions : undefined
  const peekedCustom = !isNil(peekedWording)
  const appliedTo = languageBlock?.nodeIds ?? []
  const languageNames = (codes) =>
    codes.map((code) => languageLabelFromCatalog(catalogLanguages, code)).join(', ')
  // A pending change with no preview to show for it: the field is still
  // describing the saved set, so say which set that is.
  const rulesContext =
    dirty && !usePreview
      ? `These rules are for the saved languages (${languageNames(
          stored.languages
        )}). Save your change to regenerate them.`
      : null
  // Same presence rule: speak only about a change the operator made. Omitting
  // keeps the stored wording, so a plain language save must not resend it.
  const languageInstructionsForSave = () => {
    if (!wordingTouched) {
      return undefined
    }
    if (restoringGenerated) {
      return storedCustom ? null : undefined
    }
    return draftWording === untouchedWording ? undefined : draftWording
  }

  // react-select hands back the chips in their current order — keep it.
  const handleLanguagesChange = (options) => {
    const next = (options ?? []).map((option) => option.value)
    const startLanguage = next.includes(shown.startLanguage) ? shown.startLanguage : next[0] ?? null
    setDraft({ ...(draft ?? stored), languages: next, startLanguage })
  }

  const handleStartChange = (option) => {
    setDraft({ ...(draft ?? stored), startLanguage: option?.value ?? shown.languages[0] ?? null })
  }

  // The block is generated from the set the BE stores, so a save sends the
  // settings and nothing else — the BE forbids extra fields outright.
  const handleSave = async () => {
    const result = await dispatch(
      saveLanguageSettings(agentId, {
        languages: draft.languages,
        startLanguage: draft.startLanguage,
        languageInstructions: languageInstructionsForSave(),
      })
    )
    if (!result) {
      return
    }
    if (isMultilingual(draft.languages)) {
      setRulesOpen(true)
    }
    setDraft(null)
    setPeekVersion(null)
  }

  const confirmRevert = async () => {
    if (!revertTarget) {
      return
    }
    const result = await dispatch(
      revertLanguageSettings(agentId, revertTarget.version, {
        wordingOnly: revertTarget.wordingOnly === true,
      })
    )
    if (result) {
      setRevertTarget(null)
      setPeekVersion(null)
      setDraft(null)
    }
  }

  const confirmReset = async () => {
    const result = await dispatch(resetLanguageSettings(agentId))
    if (result) {
      setResetConfirmOpen(false)
      setPeekVersion(null)
      setDraft(null)
    }
  }

  const handlePeek = (versionNumber) => {
    if (versionNumber === latestVersionNumber) {
      setPeekVersion(null)
      return
    }
    setPeekVersion(peekVersion === versionNumber ? null : versionNumber)
  }

  const handleToggleHistory = () => {
    if (historyOpen) {
      setPeekVersion(null)
    }
    setHistoryOpen(!historyOpen)
  }

  // Cancelling drops the failed attempt too — otherwise the stale error paints
  // against the next, never-submitted draft.
  const handleCancel = () => {
    setDraft(null)
    dispatch(setError({ saveLanguages: null }))
  }

  // One inline slot: the save error only while an unsaved draft is live (so a
  // stale one can't paint over a fresh stored set); revert, reset and sync
  // errors whenever present — revert is only reachable while viewing an older
  // version, so gating the slot on `!isViewing` would swallow it entirely.
  const inlineError = (!isViewing && dirty && saveError) || revertError || resetError
  // A 502 still exists — the voice-compatibility check's own provider lookup can
  // fail — and it still means nothing was stored, so the retry advice stays.
  // What went with the provider write is its per-agent shape: the old
  // `{message, agents[]}` body, the admin-only gating of those lines, and any
  // suggestion of Sync as an alternative remedy. It now arrives as a plain
  // string and retrying the save is the only thing to offer.
  const saveFailed = !isViewing && dirty && Boolean(saveError)

  return (
    <div
      className="voice-agents__override-editor voice-agents__side-card"
      data-testid="language-panel"
    >
      <div className="voice-agents__override-editor-head">
        <div className="voice-agents__override-editor-title">
          <span className="voice-agents__override-editor-avatar">
            <IconLanguage size={14} />
          </span>
          <span className="voice-agents__override-editor-label">Languages</span>
        </div>
        {versionCount > 0 ? (
          <button
            type="button"
            className="voice-agents__override-editor-edits-toggle"
            onClick={handleToggleHistory}
            data-testid={`${TEST_ID}-history-toggle`}
          >
            {historyOpen ? (
              <>
                <IconMinus size={14} /> Hide edits
              </>
            ) : (
              `All edits (${versionCount})`
            )}
          </button>
        ) : (
          <span className="voice-agents__override-editor-edits-toggle voice-agents__override-editor-edits-toggle--empty">
            All edits (0)
          </span>
        )}
      </div>

      <div className="voice-agents__language-form">
        <SettingsField label="Languages" htmlFor={`${TEST_ID}-languages`}>
          <Select
            inputId={`${TEST_ID}-languages`}
            isMulti
            options={languageOptions}
            value={shown.languages.map(optionFor)}
            onChange={handleLanguagesChange}
            isDisabled={controlsDisabled}
            placeholder="Pick at least one language"
          />
        </SettingsField>

        <SettingsField label="Starts in" htmlFor={`${TEST_ID}-start`}>
          <Select
            inputId={`${TEST_ID}-start`}
            options={startOptions}
            value={shown.startLanguage}
            onChange={handleStartChange}
            isClearable={false}
            isDisabled={controlsDisabled || isEmpty(shown.languages)}
          />
        </SettingsField>

        {/* No ASR-model control, and no drift row (2026-09-18). The model is
            derived from the language count rather than chosen — Cartesia's
            per-call API cannot be asked for Original on a single language — and
            a save no longer writes to the provider agent, so there is no live
            agent-level state to compare against or re-apply. */}

        {!isViewing && rulesAvailable && (
          <SettingsField
            label="Language rules"
            headExtra={
              <>
                {/* Which of the two states this text is in, readable without
                    opening anything. "Generated" is not decoration: it is the
                    only thing on screen saying the wording tracks the settings,
                    and therefore that changing it is a decision. */}
                {!willBeCustom && (
                  <Pill xs dataTestId={`${TEST_ID}-rules-generated-pill`}>
                    Generated
                  </Pill>
                )}
                {willBeCustom && (
                  <Pill xs dataTestId={`${TEST_ID}-rules-custom`}>
                    Custom
                  </Pill>
                )}
                {willBeCustom && (
                  <Button
                    link
                    xs
                    onClick={() => setDraft({ ...(draft ?? stored), languageInstructions: null })}
                    data-testid={`${TEST_ID}-rules-restore`}
                  >
                    Use generated wording
                  </Button>
                )}
                <button
                  type="button"
                  className="voice-agents__override-editor-edits-toggle"
                  onClick={() => setRulesOpen(!rulesOpen)}
                  data-testid={`${TEST_ID}-rules-toggle`}
                >
                  {rulesOpen ? (
                    <>
                      <IconMinus size={14} /> Hide
                    </>
                  ) : (
                    'Show'
                  )}
                </button>
              </>
            }
          >
            {rulesOpen && (
              <>
                {rulesContext && (
                  <p className="voice-agents__llm-notes" data-testid={`${TEST_ID}-rules-context`}>
                    {rulesContext}
                  </p>
                )}
                {/* Said before the edit, not after it. Every other cue that
                    this text is yours — the Custom pill, the scope note, the
                    comparison — only appears once it already is, which is too
                    late to tell anyone it was possible. */}
                {!willBeCustom && (
                  <p className="voice-agents__llm-notes" data-testid={`${TEST_ID}-rules-edit-hint`}>
                    Generated from the languages above, and follows them. Edit it to write your own
                    wording instead.
                  </p>
                )}
                {/* Editable: an agent may own this wording, and the field is
                    defaulted to the generated text so editing it is how you
                    take it over. Until then it keeps tracking the settings. */}
                <TextArea
                  id={`${TEST_ID}-rules`}
                  rows={8}
                  value={rulesValue}
                  onChange={(event) =>
                    setDraft({ ...(draft ?? stored), languageInstructions: event.target.value })
                  }
                  data-testid={`${TEST_ID}-rules`}
                />
                {wordingHasDollar && (
                  <ErrorMessage
                    warning
                    data-testid={`${TEST_ID}-rules-dollar`}
                    content="Remove the $ — these rules are inserted before variables are substituted, so a $ would pull live call data into wording that is used exactly as written."
                  />
                )}
                {willBeCustom && (
                  <p
                    className="voice-agents__llm-notes"
                    data-testid={`${TEST_ID}-rules-scope-note`}
                  >
                    Wording only. Which languages the agent understands, and which voices it can
                    use, still follow the languages above.
                  </p>
                )}
                {comparisonAvailable && (
                  <div className="voice-agents__language-rules-compare">
                    <button
                      type="button"
                      className="voice-agents__override-editor-edits-toggle"
                      onClick={() => setCompareOpen(!compareOpen)}
                      data-testid={`${TEST_ID}-rules-compare-toggle`}
                    >
                      {compareOpen ? (
                        <>
                          <IconMinus size={14} /> Hide what the settings would say
                        </>
                      ) : (
                        'The settings would word this differently'
                      )}
                    </button>
                    {compareOpen && (
                      <div className="voice-agents__generated-panel">
                        <p
                          className="voice-agents__generated-text"
                          data-testid={`${TEST_ID}-rules-generated`}
                        >
                          {generatedText}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {!isEmpty(appliedTo) && (
                  <p className="voice-agents__llm-notes" data-testid={`${TEST_ID}-rules-applied`}>
                    {`Applied to: ${appliedTo.join(', ')}`}
                  </p>
                )}
              </>
            )}
          </SettingsField>
        )}

        {/* Peeking a version used to hide the rules entirely, which made
            restoring one a guess. Read-only and always expanded: looking at the
            wording is the reason to open history in the first place. */}
        {isViewing && rulesAvailable && (
          <SettingsField
            label="Language rules"
            headExtra={
              <>
                <Pill xs dataTestId={`${TEST_ID}-version-rules-pill`}>
                  {peekedCustom ? 'Custom' : 'Generated'}
                </Pill>
                <Button
                  link
                  xs
                  disabled={isReverting}
                  onClick={() => setRevertTarget({ ...peekEntry, wordingOnly: true })}
                  data-testid={`${TEST_ID}-version-rules-restore`}
                >
                  Restore this wording
                </Button>
              </>
            }
          >
            <div className="voice-agents__generated-panel">
              <p className="voice-agents__generated-text" data-testid={`${TEST_ID}-version-rules`}>
                {peekedCustom
                  ? peekedWording
                  : 'This version used the generated rules. Restoring it drops the current custom wording.'}
              </p>
            </div>
          </SettingsField>
        )}

        {inlineError && (
          <div data-testid={`${TEST_ID}-error`}>
            {/* The retry advice belongs to a refused *save*: reset and revert
                have their own remedies, and a validation refusal wants fixing
                rather than retrying — but a save that reached the provider and
                failed there stored nothing, so saying so is the whole point. */}
            <ErrorMessage
              content={
                saveFailed && inlineError.kind !== 'voice'
                  ? `${inlineError.message} Nothing was saved — try saving again.`
                  : inlineError.message
              }
            />
            {!isEmpty(inlineError.items) && (
              <ul className="voice-agents__language-error-list">
                {inlineError.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!isViewing && dirty && (
          <div className="voice-agents__llm-actions">
            <Button
              small
              disabled={isSaving}
              onClick={handleCancel}
              data-testid={`${TEST_ID}-cancel`}
            >
              Cancel
            </Button>
            <Button
              brand
              small
              loading={isSaving}
              disabled={isSaving || isEmpty(draft.languages)}
              onClick={handleSave}
              data-testid={`${TEST_ID}-save`}
            >
              Save
            </Button>
          </div>
        )}
      </div>

      {isViewing && (
        <VersionViewingStrip
          testIdPrefix={TEST_ID}
          version={peekEntry}
          busy={isReverting}
          onRevert={() => setRevertTarget(peekEntry)}
        />
      )}

      {historyOpen && versionCount > 0 && (
        <OverrideHistoryList
          testIdPrefix={TEST_ID}
          ordered={historyEntries}
          latestVersionNumber={latestVersionNumber}
          peekVersion={peekVersion}
          onPeek={handlePeek}
        />
      )}

      {showResetRow && (
        <div className="voice-agents__override-editor-reset-row">
          <Button
            basic
            small
            critical
            onClick={() => setResetConfirmOpen(true)}
            disabled={isResetting}
            data-testid={`${TEST_ID}-reset`}
            title="Drop the override and fall back to the seeded languages"
          >
            <IconTrash /> Reset to baseline
          </Button>
        </div>
      )}

      <ConfirmModal
        show={Boolean(revertTarget)}
        onClose={() => setRevertTarget(null)}
        title={
          revertTarget
            ? `${revertTarget.wordingOnly ? 'Restore the language rules from' : 'Revert languages to'} ${formatVersionDate(revertTarget.createdAt)}?`
            : 'Revert'
        }
        confirmLabel={revertTarget?.wordingOnly ? 'Restore' : 'Revert'}
        busyLabel={revertTarget?.wordingOnly ? 'Restoring…' : 'Reverting…'}
        busy={isReverting}
        confirmDisabled={!revertTarget}
        onConfirm={confirmRevert}
        testIdPrefix={`${TEST_ID}-revert`}
      >
        {revertTarget && (
          <p>
            This creates a <strong>new edit</strong> with the{' '}
            {revertTarget.wordingOnly ? 'language rules' : 'languages'} from{' '}
            <strong>{formatVersionDate(revertTarget.createdAt)}</strong>
            {revertTarget.wordingOnly
              ? '. The current language set is left alone, and the current rules stay in history.'
              : ' and applies them to the voice provider. The current languages stay in history.'}
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        show={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        title="Reset languages to baseline?"
        confirmLabel="Reset"
        busyLabel="Resetting…"
        busy={isResetting}
        destructive
        onConfirm={confirmReset}
        testIdPrefix={`${TEST_ID}-reset`}
      >
        <p>
          Drops the override so the agent falls back to its seeded languages. History is kept — this
          writes a new “reset” edit.
        </p>
      </ConfirmModal>
    </div>
  )
}

export default LanguageSettingsPanel
